import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from './config';
import './Chatbot.css';
import trashIcon from '../assets/img/trash-icon.svg';
import fullscreenIcon from '../assets/img/fullscreen-icon.svg';
import chatIcon from '../assets/img/chat-icon.svg';
import chatIconDark from '../assets/img/chat-icon-dark.svg';
import { useAuth } from '../App';
import ChatbotSendIcon from './ChatbotSendIcon';

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false); // Отслеживаем ориентацию экрана
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false); // Отслеживаем состояние клавиатуры
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null); // Добавляем реф для контейнера чата
  const textareaRef = useRef(null); // Реф для текстового поля
  const navigate = useNavigate();
  const location = useLocation();
  
  const { 
    isAuthenticated, 
    userRole, 
    isLoading: authLoading,
    isChatbotOpen,
    closeChatbot,
    toggleChat
  } = useAuth();

  // Определяем, является ли устройство мобильным
  const isMobile = useRef(window.innerWidth <= 768);
  // Определяем, является ли устройство iOS
  const isIOS = useRef(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
  
  // Проверяем, находимся ли на странице чат-бота
  const isOnChatbotPage = location.pathname === '/chatbot';

  // Закрываем мини-чат при переходе на страницу полноэкранного чата
  useEffect(() => {
    if (isOnChatbotPage && isChatbotOpen) {
      closeChatbot();
    }
  }, [isOnChatbotPage, closeChatbot, isChatbotOpen]);

  // Отслеживаем ориентацию для адаптивных стилей
  const applyLandscapeModeStyles = useCallback(() => {
    // Проверяем ориентацию
    const isLandscapeMode = window.matchMedia("(orientation: landscape)").matches;
    setIsLandscape(isLandscapeMode);
    
    // Теперь все стили управляются через CSS медиа-запросы
    // Никаких дополнительных классов не нужно
  }, []);
  
  // Обработка изменения размера окна и ориентации
  useEffect(() => {
    const handleResize = () => {
      isMobile.current = window.innerWidth <= 768;
      
      // Устанавливаем CSS переменную для динамической высоты viewport
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      
      applyLandscapeModeStyles();
      
      // Для мобильных устройств в горизонтальной ориентации
      if (isMobile.current && window.matchMedia("(orientation: landscape)").matches && isChatbotOpen) {
        // Добавляем отступ от панели браузера
        adjustForBrowserBar();
      }
    };
    
    // Функция для настройки отступов с учетом панели браузера
    const adjustForBrowserBar = () => {
      if (!chatContainerRef.current) return;
      
      // Определяем высоту видимой области
      const viewportHeight = window.innerHeight;
      // Добавляем дополнительный отступ снизу для панели браузера
      const chatInput = chatContainerRef.current.querySelector('.chatbot-input');
      if (chatInput) {
        chatInput.style.paddingBottom = `calc(10px + env(safe-area-inset-bottom, 0px))`;
        
        // Для iOS устройств добавляем больший отступ
        if (isIOS.current) {
          chatInput.style.paddingBottom = `calc(15px + env(safe-area-inset-bottom, 10px))`;
          chatInput.style.marginBottom = `env(safe-area-inset-bottom, 10px)`;
        }
      }
      
      // Регулируем высоту области сообщений
      const chatMessages = chatContainerRef.current.querySelector('.chatbot-messages');
      if (chatMessages) {
        const inputHeight = chatInput ? chatInput.offsetHeight : 60;
        const headerHeight = chatContainerRef.current.querySelector('.chatbot-header')?.offsetHeight || 60;
        chatMessages.style.height = `calc(${viewportHeight}px - ${headerHeight}px - ${inputHeight}px - env(safe-area-inset-bottom, 0px))`;
        chatMessages.style.maxHeight = `calc(${viewportHeight}px - ${headerHeight}px - ${inputHeight}px - env(safe-area-inset-bottom, 0px))`;
      }
    };
    
    // Проверяем начальное состояние
    handleResize();
    
    // Устанавливаем начальное значение CSS переменной
    const initialVh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${initialVh}px`);
    
    // Добавляем слушатели событий
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Добавляем небольшую задержку для корректного определения ориентации
      setTimeout(() => {
        handleResize();
        // Дополнительная обработка после изменения ориентации
        setTimeout(adjustForBrowserBar, 300);
      }, 100);
    });
    
    // Дополнительный слушатель для обновления viewport при изменениях панели браузера
    if (window.visualViewport) {
      const handleViewportChange = () => {
        const vh = window.visualViewport.height * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      
      window.visualViewport.addEventListener('resize', handleViewportChange);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      };
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isChatbotOpen, applyLandscapeModeStyles]);
  
  // Обновляем стили при изменении состояния чата
  useEffect(() => {
    if (isChatbotOpen) {
      applyLandscapeModeStyles();
      
      // Задержка для применения стилей после открытия чата
      setTimeout(() => {
        if (isMobile.current && window.matchMedia("(orientation: landscape)").matches) {
          // Прокручиваем к последнему сообщению
          scrollToBottom();
        }
      }, 300);
    }
  }, [isChatbotOpen, applyLandscapeModeStyles]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Инициализируем компонент только когда информация об авторизации загружена
    if (!authLoading) {
      setInitialized(true);
      // Добавляем задержку перед показом, чтобы избежать мерцания
      if (isAuthenticated && userRole > 1) {
        setTimeout(() => {
          setVisible(true);
        }, 300);
      }
    }
  }, [authLoading, isAuthenticated, userRole]);

  useEffect(() => {
    if (isChatbotOpen && isAuthenticated && userRole > 1 && initialized) {
      loadMessages();
    }

    // Предотвращаем прокрутку страницы, когда чат открыт (только на мобильных)
    if (isChatbotOpen && window.innerWidth <= 768) {
      // Сохраняем текущую позицию прокрутки перед блокировкой
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = scrollY.toString();
      
      // Блокировка прокрутки через CSS класс и инлайн стили
      document.body.classList.add('chatbot-scroll-locked');
      document.body.style.top = `-${scrollY}px`;
    } else {
      // Восстанавливаем прокрутку
      if (document.body.classList.contains('chatbot-scroll-locked')) {
        const scrollY = parseInt(document.body.dataset.scrollY || '0');
        
        // Убираем класс и стили
        document.body.classList.remove('chatbot-scroll-locked');
        document.body.style.top = '';
        
        // Восстанавливаем позицию прокрутки
        setTimeout(() => {
          window.scrollTo({
            top: scrollY,
            behavior: 'instant'
          });
          // Очищаем сохраненные данные
          delete document.body.dataset.scrollY;
        }, 50);
      }
    }
  }, [isChatbotOpen, isAuthenticated, userRole, initialized]);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('Токен не найден');
        return;
      }

      const response = await fetch(`${API_URL}/chatbot/message/get_messages?limit=50&offset=0`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      } else {
        console.error('Ошибка загрузки сообщений:', response.status);
      }
    } catch (error) {
      console.error('Ошибка при загрузке сообщений:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Необходимо войти в систему для использования чат-бота');
      return;
    }

    // Получаем user_id из API /current
    let userId;
    try {
      const userResponse = await fetch(`${API_URL}/current`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        userId = userData.id;
      } else {
        throw new Error('Не удалось получить данные пользователя');
      }
    } catch (error) {
      console.error('Ошибка получения данных пользователя:', error);
      alert('Ошибка: не удается определить пользователя');
      return;
    }

    const userMessage = {
      content: newMessage,
      role: 'user',
      user_id: userId,
      id: Date.now() // временный ID для отображения
    };

    // Добавляем сообщение пользователя сразу
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chatbot/message/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          content: newMessage,
          user_id: userId
        }),
      });

      if (response.ok) {
        const assistantMessage = await response.json();
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        console.error('Ошибка отправки сообщения:', response.status);
        alert('Ошибка при отправке сообщения');
      }
    } catch (error) {
      console.error('Ошибка при отправке сообщения:', error);
      alert('Ошибка при отправке сообщения');
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = async () => {
    if (!window.confirm('Вы уверены, что хотите очистить историю чата?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/chatbot/message/delete_messages`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        setMessages([]);
      } else {
        console.error('Ошибка очистки сообщений:', response.status);
      }
    } catch (error) {
      console.error('Ошибка при очистке сообщений:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Функция для обработки нажатия на кнопку вызова чат-бота
  const handleToggleClick = () => {
    console.log("Нажата кнопка вызова чат-бота внизу экрана");
    toggleChat();
  };

  // Функция для отображения текста с учетом форматирования
  const formatMessage = (text) => {
    if (!text) return '';

    // Заменяем множественные переносы строк на специальные маркеры
    const textWithMarkers = text
      .replace(/\n\n\n/g, '\n<triple-break>\n')  // Тройной перенос
      .replace(/\n\n/g, '\n<double-break>\n');   // Двойной перенос
    
    // Разделяем текст по переносам строк
    const lines = textWithMarkers.split('\n');
    
    // Обрабатываем каждую строку отдельно
    return lines.map((line, lineIndex) => {
      // Проверяем наличие специальных маркеров для множественных переносов
      if (line === '<double-break>') {
        return <br key={`br-double-${lineIndex}`} />;
      }
      
      if (line === '<triple-break>') {
        return (
          <React.Fragment key={`br-triple-${lineIndex}`}>
            <br />
            <br />
          </React.Fragment>
        );
      }
      
      // Проверяем, начинается ли строка с ###
      if (line.startsWith('### ')) {
        // Это заголовок, отображаем его как h3
        return (
          <h3 className="message-heading" key={`heading-${lineIndex}`}>
            {line.substring(4)}
          </h3>
        );
      }
      
      // Регулярное выражение для поиска текста между двойными звездочками
      const boldRegex = /\*\*(.*?)\*\*/g;
      
      // Разбиваем текст на части: обычный текст и текст, который нужно сделать жирным
      const parts = [];
      let lastIndex = 0;
      let match;
      
      while ((match = boldRegex.exec(line)) !== null) {
        // Добавляем обычный текст перед совпадением
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            content: line.slice(lastIndex, match.index)
          });
        }
        
        // Добавляем жирный текст
        parts.push({
          type: 'bold',
          content: match[1] // текст между звездочками
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // Добавляем оставшийся обычный текст после последнего совпадения
      if (lastIndex < line.length) {
        parts.push({
          type: 'text',
          content: line.slice(lastIndex)
        });
      }
      
      // Если совпадений не найдено, возвращаем исходный текст
      if (parts.length === 0) {
        return <React.Fragment key={lineIndex}>{line}</React.Fragment>;
      }
      
      // Преобразуем части в React-элементы
      const formattedLine = parts.map((part, partIndex) => {
        if (part.type === 'bold') {
          return <strong key={`${lineIndex}-${partIndex}`}>{part.content}</strong>;
        }
        return <span key={`${lineIndex}-${partIndex}`}>{part.content}</span>;
      });
      
      // Если это не последняя строка, добавляем перенос строки
      return (
        <React.Fragment key={lineIndex}>
          {formattedLine}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  // Функция для подстановки примера вопроса в поле ввода
  const insertExampleQuestion = (question) => {
    setNewMessage(question);
    // Фокусируемся на поле ввода после выбора примера
    const textarea = document.querySelector('.chatbot-input textarea');
    if (textarea) {
      textarea.focus();
    }
  };

  // Проверяем текущую тему при монтировании компонента
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark-mode'));
    };
    
    // Проверяем текущее состояние
    checkDarkMode();
    
    // Добавляем слушатель события изменения темы
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    // Удаляем слушатель при размонтировании
    return () => observer.disconnect();
  }, []);

  // Проверяем полноэкранный режим карты
  useEffect(() => {
    const checkMapFullscreen = () => {
      const isMapFullscreen = 
        document.body.classList.contains('map-fullscreen') || 
        document.body.classList.contains('map-fullscreen-ios');
      
      // Если карта в полноэкранном режиме, скрываем чат-бот
      if (isMapFullscreen && isChatbotOpen) {
        closeChatbot();
      }

      // Обновляем видимость кнопки чат-бота
      setVisible(!isMapFullscreen && isAuthenticated && userRole > 1 && !isOnChatbotPage);
    };
    
    // Проверяем текущее состояние
    checkMapFullscreen();
    
    // Добавляем слушатель события изменения класса body
    const observer = new MutationObserver(checkMapFullscreen);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    // Удаляем слушатель при размонтировании
    return () => observer.disconnect();
  }, [isAuthenticated, userRole, isOnChatbotPage, isChatbotOpen, closeChatbot]);

  return (
    <div className={`chatbot-wrapper ${visible ? 'visible' : 'hidden'}`}>
      {isAuthenticated && userRole > 1 && !isOnChatbotPage && (
        <>
          {/* Кнопка вызова чат-бота, скрываем если мы на странице полноэкранного чата */}
          <div 
            className={`chatbot-toggle ${isChatbotOpen ? 'open' : ''}`} 
            onClick={handleToggleClick}
          >
            <img 
              src={isDarkMode ? chatIconDark : chatIcon} 
              alt="Чат" 
              className="chatbot-icon" 
            />
          </div>
          
          <div className={`chatbot-overlay ${isChatbotOpen ? 'open' : ''}`} onClick={closeChatbot}></div>
          
          <div 
            ref={chatContainerRef}
            className={`chatbot-container ${isChatbotOpen ? 'open' : ''} ${isKeyboardOpen ? 'keyboard-open' : ''}`}
          >
            <div className="chatbot-header">
              <h3>AI Ассистент</h3>
              <div className="chatbot-controls">
                <button className="clear-btn" onClick={clearMessages} title="Очистить чат">
                  <img src={trashIcon} alt="Очистить" className="trash-icon" />
                </button>
                {/* Кнопка полноэкранного режима, видимая только на десктопе или когда не в ландшафтной ориентации */}
                {(!isMobile.current || (isMobile.current && !isLandscape)) && (
                  <button 
                    className="expand-btn" 
                    onClick={() => {
                      // Сохраняем текущую позицию прокрутки перед переходом
                      const scrollY = window.scrollY;
                      sessionStorage.setItem('chatbot_scroll_position', scrollY.toString());
                      
                      // Закрываем мини-чат и переходим на страницу полноэкранного чата
                      closeChatbot();
                      navigate('/chatbot');
                    }} 
                    title="Полноэкранный режим"
                  >
                    <img src={fullscreenIcon} alt="Полноэкранный режим" className="fullscreen-icon" />
                  </button>
                )}
                <button className="chatbot-close-btn" onClick={closeChatbot} title="Закрыть">
                  ✖
                </button>
              </div>
            </div>
            
            <div className="chatbot-messages">
              {messages.length === 0 ? (
                <div className="empty-chat">
                  <p>Я ваш AI ассистент. Задайте мне вопрос о недвижимости, и я постараюсь помочь!</p>
                  {/* Показываем примеры вопросов только для полноэкранного режима */}
                  {isOnChatbotPage && (
                    <div className="example-questions-mini">
                      <h4>Примеры вопросов:</h4>
                      <ul>
                        <li onClick={() => insertExampleQuestion("Расскажи о состоянии дома по адресу:")}>
                          Расскажи о состоянии дома по адресу:
                        </li>
                        <li onClick={() => insertExampleQuestion("Посоветуй дом для покупки квартиры")}>
                          Посоветуй дом для покупки квартиры
                        </li>
                        <li onClick={() => insertExampleQuestion("Какие дома сейчас в плохом состоянии")}>
                          Какие дома сейчас в плохом состоянии
                        </li>
                        <li onClick={() => insertExampleQuestion("Где самые не дорогие квартиры")}>
                          Где самые не дорогие квартиры
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}>
                    <div className="message-content">
                      {formatMessage(msg.content)}
                    </div>
                    <div className="message-role">
                      {msg.role === 'user' ? 'Вы' : 'AI Ассистент'}
                    </div>
                  </div>
                ))
              )}
              {isLoading && (
                <div className="message assistant-message ai-thinking">
                  <div className="message-content">
                    <div className="thinking-indicator">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="thinking-text">AI думает...</span>
                    </div>
                  </div>
                  <div className="message-role">
                    AI Ассистент
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <div className="chatbot-input">
              <textarea
                ref={textareaRef}
                placeholder="Введите ваше сообщение..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                onFocus={() => {
                  // Для мобильных устройств: при фокусе на поле ввода
                  if (isMobile.current) {
                    setIsKeyboardOpen(true);
                    const container = document.querySelector('.chatbot-container');
                    if (container) {
                      // Добавляем класс для корректировки размера на мобильных
                      container.classList.add('keyboard-open');
                      
                      // Для горизонтальной ориентации дополнительно настраиваем
                      if (isLandscape) {
                        // Прокручиваем к низу чата с задержкой
                        setTimeout(scrollToBottom, 300);
                        
                        // Дополнительная обработка для панели браузера
                        const chatInput = container.querySelector('.chatbot-input');
                        if (chatInput) {
                          chatInput.style.paddingBottom = `calc(15px + env(safe-area-inset-bottom, 10px))`;
                        }
                      }
                    }
                    // Прокручиваем к низу чата
                    setTimeout(scrollToBottom, 300);
                  }
                }}
                onBlur={() => {
                  // Для мобильных устройств: при потере фокуса
                  if (isMobile.current) {
                    setIsKeyboardOpen(false);
                    setTimeout(() => {
                      const container = document.querySelector('.chatbot-container');
                      if (container) {
                        // Удаляем класс
                        container.classList.remove('keyboard-open');
                        
                        // Для горизонтальной ориентации
                        if (isLandscape) {
                          // Восстанавливаем стандартные отступы
                          const chatInput = container.querySelector('.chatbot-input');
                          if (chatInput) {
                            chatInput.style.paddingBottom = `calc(10px + env(safe-area-inset-bottom, 0px))`;
                          }
                        }
                      }
                    }, 100);
                  }
                }}
              />
              <button
                className="send-btn"
                onClick={sendMessage}
                disabled={!newMessage.trim() || isLoading}
              >
                <ChatbotSendIcon isLoading={false} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Chatbot; 